/**
 * After-call grader tab.
 *
 * Top: scoring model + base system prompt (reset-to-default, Custom/Default
 * badge) → PUT /api/admin/ai-config (api.adminSaveGraderConfig). Blank prompt
 * falls back to the server's built-in DEFAULT_SYSTEM_PROMPT.
 *
 * Bottom (per call type): a call_type selector → that type's guidance textarea
 * (reset-to-default; '' clears it back to the code TYPE_GUIDANCE default) saved
 * via the same ai-config PUT, AND a rubric editor (label / weight / anchor rows
 * with a live weight TOTAL). Saving the rubric is gated on TOTAL === 100 and
 * writes a NEW version via PUT /api/admin/rubric.
 *
 * Footer: "Re-score all calls" (POST /api/admin/rescore) — estimate (count +
 * AI $ from stored transcripts) → explicit confirm → run. It re-enqueues
 * classify+score off the EXISTING transcripts; it never re-runs STT. Lives here
 * because re-scoring is what you do right after changing the rubric/prompt.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { CallType, RubricCriterionEdit } from "@/types";
import { REAL_CALL_TYPES } from "@/types";
import { Button, Card, SelectField, Badge } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import { centsToDollars } from "@/lib/format";
import {
  ModelCard,
  SystemPromptCard,
  SavedNote,
  ErrorNote,
  NumberField,
} from "./shared";

const GRADER_KEY = ["admin", "grader-config"] as const;
const rubricKey = (ct: CallType) => ["admin", "rubric", ct] as const;

// Friendly labels for the 5 real call types (unknown is never editable here).
const CALL_TYPE_LABELS: Record<string, string> = {
  automotive_tint: "Automotive tint",
  ppf: "Paint protection film (PPF)",
  arch_residential: "Architectural — residential",
  arch_commercial: "Architectural — commercial",
  non_sales_admin: "Non-sales / admin",
};

function callTypeLabel(ct: string): string {
  return CALL_TYPE_LABELS[ct] ?? ct;
}

export function GraderTab() {
  const qc = useQueryClient();

  const configQuery = useQuery({
    queryKey: GRADER_KEY,
    queryFn: ({ signal }) => api.adminGetGraderConfig(signal),
  });

  // ── Base model + base system prompt ──────────────────────────────────────
  const [model, setModel] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [baseSaved, setBaseSaved] = useState(false);

  // ── Per-type guidance ────────────────────────────────────────────────────
  const [selectedType, setSelectedType] = useState<CallType>(
    REAL_CALL_TYPES[0] ?? "automotive_tint",
  );
  const [guidance, setGuidance] = useState("");
  const [guidanceSaved, setGuidanceSaved] = useState(false);

  const data = configQuery.data;

  // Hydrate base fields + the selected type's guidance whenever fresh data lands.
  useEffect(() => {
    if (data) {
      setModel(data.model);
      setSystemPrompt(data.system_prompt);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      setGuidance(data.type_guidance[selectedType]?.text ?? "");
      setGuidanceSaved(false);
    }
  }, [data, selectedType]);

  const saveBase = useMutation({
    mutationFn: (body: { model?: string; system_prompt?: string }) =>
      api.adminSaveGraderConfig(body),
    onSuccess: () => {
      // PUT returns {ok,changed}; the GET refetch supplies the authoritative config.
      void qc.invalidateQueries({ queryKey: GRADER_KEY });
      setBaseSaved(true);
    },
  });

  const saveGuidance = useMutation({
    mutationFn: (text: string) =>
      api.adminSaveGraderConfig({ type_guidance: { [selectedType]: text } }),
    onSuccess: () => {
      // PUT returns {ok,changed}; the GET refetch supplies the authoritative config.
      void qc.invalidateQueries({ queryKey: GRADER_KEY });
      setGuidanceSaved(true);
    },
  });

  if (configQuery.isLoading) {
    return <LoadingState label="Loading grader settings…" />;
  }
  if (configQuery.isError || !data) {
    return (
      <ErrorState
        error={configQuery.error}
        onRetry={() => void configQuery.refetch()}
      />
    );
  }

  const baseIsCustom = data.system_prompt_is_custom;
  const baseDirty =
    model !== data.model || systemPrompt !== data.system_prompt;
  const baseSaving = saveBase.isPending;

  const guidanceEntry = data.type_guidance[selectedType];
  const guidanceIsCustom = guidanceEntry?.is_custom ?? false;
  const guidanceServerText = guidanceEntry?.text ?? "";
  const guidanceDirty = guidance !== guidanceServerText;
  const guidanceSaving = saveGuidance.isPending;

  const typeOptions = REAL_CALL_TYPES.map((ct) => ({
    value: ct,
    label: callTypeLabel(ct),
  }));

  return (
    <div className="flex flex-col gap-6">
      {/* ── Base model ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Scoring engine</h2>
          <p className="text-sm text-muted-foreground">
            The model and base prompt used to grade flagged calls against the
            rubric.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SavedNote show={baseSaved && !baseDirty} />
          <Button
            onClick={() => {
              setBaseSaved(false);
              saveBase.mutate({ model, system_prompt: systemPrompt });
            }}
            disabled={baseSaving || !baseDirty}
          >
            {baseSaving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <ModelCard
        label="Scoring model"
        help="The model Claude uses to grade each flagged call against your rubric."
        options={data.model_options}
        value={model}
        onChange={(v) => {
          setBaseSaved(false);
          setModel(v);
        }}
      />

      <SystemPromptCard
        title="Base system prompt"
        value={systemPrompt}
        onChange={(v) => {
          setBaseSaved(false);
          setSystemPrompt(v);
        }}
        isCustom={baseIsCustom}
        rows={20}
        onReset={() => {
          setBaseSaved(false);
          setSystemPrompt("");
          saveBase.mutate({ model, system_prompt: "" });
        }}
        resetDisabled={baseSaving || !baseIsCustom}
        help={
          <>
            The{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono">
              {"{{RUBRIC_JSON}}"}
            </code>{" "}
            placeholder is auto-filled with each call&apos;s current per-type
            rubric. Leave blank to use the built-in default.
          </>
        }
        errorSlot={<ErrorNote show={saveBase.isError} />}
      />

      {/* ── Per call type ──────────────────────────────────────────────────── */}
      <Card className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">Per call type</h2>
          <SelectField
            label="Call type"
            options={typeOptions}
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as CallType)}
            className="max-w-xs"
          />
        </div>

        {/* Guidance for the selected type */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium">
              Guidance — {callTypeLabel(selectedType)}
            </h3>
            <Badge tone={guidanceIsCustom ? "primary" : "muted"}>
              {guidanceIsCustom ? "Custom" : "Default"}
            </Badge>
          </div>
          <textarea
            value={guidance}
            onChange={(e) => {
              setGuidanceSaved(false);
              setGuidance(e.target.value);
            }}
            rows={8}
            spellCheck={false}
            placeholder="Leave blank to use the built-in guidance for this call type…"
            className="w-full rounded-md border bg-background px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-sm text-muted-foreground">
            Extra coaching context injected when grading this call type. Leave
            blank to use the built-in default for {callTypeLabel(selectedType)}.
          </p>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => {
                setGuidanceSaved(false);
                saveGuidance.mutate(guidance);
              }}
              disabled={guidanceSaving || !guidanceDirty}
            >
              {guidanceSaving ? "Saving…" : "Save guidance"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setGuidanceSaved(false);
                setGuidance("");
                saveGuidance.mutate("");
              }}
              disabled={guidanceSaving || !guidanceIsCustom}
            >
              Reset to default
            </Button>
            <SavedNote show={guidanceSaved && !guidanceDirty} />
            <ErrorNote show={saveGuidance.isError} />
          </div>
        </div>

        {/* Rubric editor for the selected type */}
        <RubricEditorSection callType={selectedType} />
      </Card>

      {/* Re-score everything against the current rubric/prompts/models */}
      <RescoreSection />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Bulk re-score                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Estimate-then-confirm bulk re-score. The estimate shows the call count + the
 * projected Claude cost BEFORE anything runs; only the explicit "Run re-score"
 * click enqueues. Reuses stored transcripts — never re-runs speech-to-text.
 */
function RescoreSection() {
  const [estimate, setEstimate] = useState<{ count: number; est_cost_cents: number } | null>(
    null,
  );
  const [doneCount, setDoneCount] = useState<number | null>(null);

  const estimateRun = useMutation({
    mutationFn: () => api.adminRescoreEstimate(),
    onSuccess: (r) => {
      setEstimate(r);
      setDoneCount(null);
    },
  });

  const run = useMutation({
    mutationFn: () => api.adminRescoreRun(),
    onSuccess: (r) => {
      setDoneCount(r.enqueued);
      setEstimate(null);
    },
  });

  const busy = estimateRun.isPending || run.isPending;

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Re-score all calls</h2>
          <p className="text-sm text-muted-foreground">
            Re-classifies and re-grades every call that already has a transcript, using
            the current rubric, prompts, and models. Reuses the stored transcripts — it
            never re-runs speech-to-text, so there&apos;s no STT cost.
          </p>
        </div>
        {estimate === null && (
          <Button
            variant="secondary"
            onClick={() => estimateRun.mutate()}
            disabled={busy}
          >
            {estimateRun.isPending ? "Estimating…" : "Re-score all…"}
          </Button>
        )}
      </div>

      {/* Confirm step — nothing runs until this explicit click. */}
      {estimate !== null && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/40">
          {estimate.count === 0 ? (
            <>
              <p className="text-amber-800 dark:text-amber-300">
                No transcribed calls to re-score.
              </p>
              <div className="mt-2">
                <Button variant="ghost" onClick={() => setEstimate(null)} disabled={busy}>
                  Close
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="font-medium text-amber-800 dark:text-amber-300">
                Re-score {estimate.count} call{estimate.count === 1 ? "" : "s"} for an
                estimated {centsToDollars(estimate.est_cost_cents)} in AI cost?
              </p>
              <p className="mt-1 text-amber-800/90 dark:text-amber-300/90">
                Reuses the existing transcripts — never re-runs speech-to-text. Calls
                re-classify and re-grade in the background; current scores are replaced
                as each finishes.
              </p>
              <div className="mt-3 flex items-center gap-3">
                <Button onClick={() => run.mutate()} disabled={busy}>
                  {run.isPending ? "Enqueuing…" : "Run re-score"}
                </Button>
                <Button variant="ghost" onClick={() => setEstimate(null)} disabled={busy}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {doneCount !== null && (
        <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
          Enqueued {doneCount} call{doneCount === 1 ? "" : "s"} for re-scoring — they&apos;ll
          process in the background (watch the Audit log tab for runs landing).
        </p>
      )}
      {(estimateRun.isError || run.isError) && (
        <p className="text-sm text-red-600">
          {run.isError
            ? "Couldn't start the re-score. Please try again."
            : "Couldn't estimate the re-score. Please try again."}
        </p>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Rubric editor                                                              */
/* -------------------------------------------------------------------------- */

const BLANK_CRITERION = (sort: number): RubricCriterionEdit => ({
  key: "",
  label: "",
  weight: 0,
  sort,
  full_credit: "",
  partial_credit: "",
  zero_credit: "",
});

function RubricEditorSection({ callType }: { callType: CallType }) {
  const qc = useQueryClient();

  const rubricQuery = useQuery({
    queryKey: rubricKey(callType),
    queryFn: ({ signal }) => api.adminGetRubric(callType, signal),
  });

  const [criteria, setCriteria] = useState<RubricCriterionEdit[]>([]);
  const [note, setNote] = useState("");
  const [savedVersion, setSavedVersion] = useState<number | null>(null);

  const data = rubricQuery.data;
  useEffect(() => {
    if (data) {
      setCriteria(data.criteria);
      setNote("");
      setSavedVersion(null);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (body: {
      call_type: CallType;
      note?: string;
      criteria: RubricCriterionEdit[];
    }) => api.adminSaveRubric(body),
    onSuccess: (res: { version: number }) => {
      setSavedVersion(res.version);
      setNote("");
      // Refetch so the editor reflects the new current version + reset dirty.
      void qc.invalidateQueries({ queryKey: rubricKey(callType) });
    },
  });

  if (rubricQuery.isLoading) {
    return <LoadingState label="Loading rubric…" />;
  }
  if (rubricQuery.isError || !data) {
    return (
      <ErrorState
        error={rubricQuery.error}
        onRetry={() => void rubricQuery.refetch()}
      />
    );
  }

  const total = criteria.reduce(
    (sum, c) => sum + (Number.isFinite(c.weight) ? c.weight : 0),
    0,
  );
  const totalOk = total === 100;
  const keys = criteria.map((c) => c.key.trim());
  const hasBlankKey = keys.some((k) => k.length === 0);
  const hasDupKey = new Set(keys).size !== keys.length;
  const hasRows = criteria.length > 0;
  const saving = save.isPending;
  const canSave =
    totalOk && hasRows && !hasBlankKey && !hasDupKey && !saving;

  function update(i: number, patch: Partial<RubricCriterionEdit>) {
    setSavedVersion(null);
    setCriteria((rows) =>
      rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    );
  }
  function remove(i: number) {
    setSavedVersion(null);
    setCriteria((rows) => rows.filter((_, idx) => idx !== i));
  }
  function add() {
    setSavedVersion(null);
    setCriteria((rows) => [...rows, BLANK_CRITERION(rows.length)]);
  }

  return (
    <div className="flex flex-col gap-3 border-t pt-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium">
            Rubric — {callTypeLabel(callType)}
          </h3>
          <p className="text-xs text-muted-foreground">
            Current version v{data.version}. Saving creates a new version;
            previously scored calls keep their old rubric.
          </p>
        </div>
        <Badge tone={totalOk ? "success" : "danger"}>Total: {total} / 100</Badge>
      </div>

      <div className="flex flex-col gap-4">
        {criteria.map((c, i) => (
          <div key={i} className="rounded-md border p-3">
            <div className="grid gap-3 sm:grid-cols-[1fr_1fr_120px]">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">Key</span>
                <input
                  value={c.key}
                  onChange={(e) => update(i, { key: e.target.value })}
                  placeholder="captured_customer_vehicle"
                  className="rounded-md border bg-background px-3 py-1.5 font-mono text-xs outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">Label</span>
                <input
                  value={c.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                  placeholder="Captured customer & vehicle"
                  className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
              <NumberField
                label="Weight"
                value={c.weight}
                min={0}
                onChange={(v) => update(i, { weight: Math.max(0, Math.round(v)) })}
              />
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <AnchorField
                label="Full credit"
                value={c.full_credit}
                onChange={(v) => update(i, { full_credit: v })}
              />
              <AnchorField
                label="Partial credit"
                value={c.partial_credit}
                onChange={(v) => update(i, { partial_credit: v })}
              />
              <AnchorField
                label="Zero credit"
                value={c.zero_credit}
                onChange={(v) => update(i, { zero_credit: v })}
              />
            </div>

            <div className="mt-2 flex justify-end">
              <Button
                variant="ghost"
                className="px-2 py-1 text-xs text-red-600"
                onClick={() => remove(i)}
              >
                Delete criterion
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div>
        <Button variant="ghost" className="text-xs" onClick={add}>
          + Add criterion
        </Button>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Version note (optional)</span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What changed in this version…"
          className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
        />
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={() =>
            save.mutate({
              call_type: callType,
              note: note.trim() || undefined,
              criteria,
            })
          }
          disabled={!canSave}
          title={
            !totalOk
              ? "Weights must total exactly 100 to save"
              : hasBlankKey
                ? "Every criterion needs a key"
                : hasDupKey
                  ? "Criterion keys must be unique"
                  : undefined
          }
        >
          {saving ? "Saving…" : "Save rubric (new version)"}
        </Button>

        {!totalOk && (
          <span className="text-sm text-amber-600 dark:text-amber-400">
            Weights must total exactly 100 (currently {total}).
          </span>
        )}
        {totalOk && hasBlankKey && (
          <span className="text-sm text-amber-600 dark:text-amber-400">
            Every criterion needs a non-empty key.
          </span>
        )}
        {totalOk && !hasBlankKey && hasDupKey && (
          <span className="text-sm text-amber-600 dark:text-amber-400">
            Criterion keys must be unique.
          </span>
        )}
        {savedVersion !== null && (
          <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
            Saved as v{savedVersion}.
          </span>
        )}
        {save.isError && (
          <span className="text-sm text-red-600">
            Couldn&apos;t save the rubric. Please try again.
          </span>
        )}
      </div>
    </div>
  );
}

function AnchorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        spellCheck={false}
        className="w-full rounded-md border bg-background px-3 py-2 text-xs leading-relaxed outline-none focus:ring-2 focus:ring-ring"
      />
    </label>
  );
}
